import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  Box,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  Paper,
  Typography,
  Checkbox,
  Button,
  CircularProgress,
  Grid,
} from "@mui/material";
import { LineItemDto } from "../../../../../../../api-client";
import useStore from "../../../../../../../helpers/useStore";
import { TransactionsContext } from "../../../../../../../stores/Transactions/Transactions.provider";

interface LineItemSelectorProps {
  rxId: string;
  rxData: any;
  selectedLineItems: LineItemDto[];
  onSelectionChange: (selectedItems: LineItemDto[]) => void;
}

const formatDate = (d: Date) =>
  d.toLocaleDateString("de-DE", {
    year: "2-digit",
    month: "2-digit",
    day: "2-digit",
  });

const LineItemSelector: React.FC<LineItemSelectorProps> = ({
  rxId,
  rxData,
  selectedLineItems,
  onSelectionChange,
}) => {
  const [allLineItems, setAllLineItems] = useState<LineItemDto[]>([]);
  const [loading, setLoading] = useState(false);
  const TransactionStore = useStore(TransactionsContext);

  // Load all line items for this Rx
  useEffect(() => {
    if (!rxId) return;

    const load = async () => {
      setLoading(true);
      try {
        const items = await TransactionStore.getLineItemsForRx(rxId);
        setAllLineItems(items);
        // If no selection exists yet, default to all items selected
        if (selectedLineItems.length === 0 && items.length > 0) {
          onSelectionChange(items);
        }
      } catch (error) {
        console.error("Failed to load line items:", error);
      } finally {
        setLoading(false);
      }
    };
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rxId]);

  // Helper to check if an item is selected
  const isItemSelected = useCallback(
    (item: LineItemDto) =>
      selectedLineItems.some(
        (selected) =>
          selected.priceId === item.priceId &&
          selected.code === item.code &&
          new Date(selected.date).getTime() === new Date(item.date).getTime(),
      ),
    [selectedLineItems],
  );

  const isAllSelected =
    allLineItems.length > 0 && allLineItems.every(isItemSelected);
  const selectedCount = selectedLineItems.length;
  const totalCount = allLineItems.length;

  /**
   * Compute netValue matching the backend computeNetValueFromLineItems:
   *   netValue = grossValue - sum(item.copayment)
   * No flat Rezeptgebühr — the original bill already took it.
   */
  const selectedTotal = useMemo(() => {
    if (selectedLineItems.length === 0) return 0;

    const grossValue = selectedLineItems.reduce(
      (sum, item) => sum + Number(item.price || 0),
      0,
    );
    const copayment = selectedLineItems.reduce(
      (sum, item) => sum + Number(item.copayment || 0),
      0,
    );

    return Math.round((grossValue - copayment) * 100) / 100;
  }, [selectedLineItems]);

  const handleToggleItem = useCallback(
    (item: LineItemDto) => {
      if (isItemSelected(item)) {
        // Deselect: remove this item
        const newSelection = selectedLineItems.filter(
          (selected) =>
            !(
              selected.priceId === item.priceId &&
              selected.code === item.code &&
              new Date(selected.date).getTime() ===
                new Date(item.date).getTime()
            ),
        );
        onSelectionChange(newSelection);
      } else {
        // Select: add this item
        onSelectionChange([...selectedLineItems, item]);
      }
    },
    [isItemSelected, selectedLineItems, onSelectionChange],
  );

  const handleToggleAll = useCallback(() => {
    if (isAllSelected) {
      onSelectionChange([]);
    } else {
      onSelectionChange([...allLineItems]);
    }
  }, [isAllSelected, allLineItems, onSelectionChange]);

  // Patient info from rxData
  const patientName = rxData?.patient
    ? `${rxData.patient.lastName}, ${rxData.patient.firstName}`
    : "";
  const rxNumber = rxData?.rxNumber || "";
  const rxDate = rxData?.date
    ? new Date(rxData.date).toLocaleDateString("de-DE", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
      })
    : "";

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
      {/* Header */}

      {/* Toolbar */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          px: 1,
          mb: 1,
        }}
      >
        <Button
          size="small"
          variant="outlined"
          onClick={handleToggleAll}
          sx={{ borderRadius: 2, textTransform: "none" }}
        >
          {isAllSelected ? "Alle abwählen" : "Alle auswählen"}
        </Button>
        <Typography variant="body2" color="textSecondary">
          {selectedCount} / {totalCount} Positionen ausgewählt
        </Typography>
      </Box>

      {/* Loading */}
      {loading && (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress size={28} />
        </Box>
      )}

      {/* Table */}
      {!loading && allLineItems.length > 0 && (
        <TableContainer
          component={Paper}
          className="table-container"
          sx={{ flexGrow: 1 }}
        >
          <Table stickyHeader size="medium">
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    checked={isAllSelected}
                    indeterminate={selectedCount > 0 && !isAllSelected}
                    onChange={handleToggleAll}
                    size="medium"
                  />
                </TableCell>
                <TableCell sx={{ fontSize: "0.95rem" }}>Kürzel</TableCell>
                <TableCell sx={{ fontSize: "0.95rem" }}>Code</TableCell>
                <TableCell align="right" sx={{ fontSize: "0.95rem" }}>
                  Preis
                </TableCell>
                <TableCell align="right" sx={{ fontSize: "0.95rem" }}>
                  Zuzahlung
                </TableCell>
                <TableCell sx={{ fontSize: "0.95rem", textAlign: "right" }}>
                  Datum
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {allLineItems.map((item, index) => {
                const isSelected = isItemSelected(item);
                return (
                  <TableRow
                    key={index}
                    onClick={() => handleToggleItem(item)}
                    sx={{
                      cursor: "pointer",
                      backgroundColor: isSelected ? "#06465D" : "inherit",
                      color: isSelected ? "white" : "inherit",
                      "&:hover": {
                        backgroundColor: isSelected
                          ? "#06465D !important"
                          : "rgba(0, 0, 0, 0.04)",
                      },
                      "& .MuiTableCell-root": {
                        color: isSelected ? "white" : "inherit",
                        fontSize: "1rem",
                        py: 1.5,
                      },
                    }}
                  >
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={isSelected}
                        size="medium"
                        sx={{
                          color: isSelected ? "white" : "inherit",
                          "&.Mui-checked": {
                            color: "white",
                          },
                        }}
                      />
                    </TableCell>
                    <TableCell title={item.description}>
                      {item.abbreviation}
                    </TableCell>
                    <TableCell>{item.code}</TableCell>
                    <TableCell align="right">
                      {Number(item.price).toFixed(2).replace(".", ",")} &euro;
                    </TableCell>
                    <TableCell align="right">
                      {Number(item.copayment).toFixed(2).replace(".", ",")}{" "}
                      &euro;
                    </TableCell>
                    <TableCell align="right">
                      {formatDate(new Date(item.date))}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Empty state */}
      {!loading && allLineItems.length === 0 && (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <Typography variant="body2" color="textSecondary">
            Keine Positionen verfügbar
          </Typography>
        </Box>
      )}

      {/* Footer — total */}
      {allLineItems.length > 0 && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            px: 2,
            py: 1.5,
            mt: 1,
            backgroundColor: "#f5f5f5",
            borderRadius: "0 0 10px 10px",
            borderTop: "1px solid #ddd",
          }}
        >
          <Typography variant="subtitle2" fontWeight="bold">
            Gesamt (ausgewählt)
          </Typography>
          <Typography variant="subtitle1" fontWeight="bold">
            {selectedTotal.toFixed(2).replace(".", ",")} &euro;
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default LineItemSelector;
